import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { COMPANY, API } from "@/data/content";

const CARDS = [
  { icon: Phone, label: "Call Anu directly", value: COMPANY.phoneDisplay, href: `tel:${COMPANY.phone}`, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: "contact-email" },
  { icon: MapPin, label: "Office", value: COMPANY.address, href: "https://maps.google.com/?q=Camberwell+VIC+3124", testid: "contact-address" },
  { icon: Clock, label: "Hours", value: COMPANY.hours, testid: "contact-hours" },
];

const inputCls =
  "w-full rounded-xl border border-ink/10 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:ring-2 focus:ring-gold/60 focus:border-gold transition-all duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const valid = form.name.trim().length >= 2 && form.email.includes("@") && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-36 sm:pt-44 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-[1.05] max-w-3xl">
            Based in Camberwell. <span className="font-editorial italic font-medium text-gold-600">Advising Australia-wide.</span>
          </h1>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {CARDS.map((c, i) => (
            <Reveal key={c.label} delay={0.06 * i}>
              <a
                href={c.href || undefined}
                target={c.href?.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={c.testid}
                className="block h-full rounded-3xl bg-white border border-ink/8 p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lift"
              >
                <c.icon size={22} className="text-brand mb-5" strokeWidth={1.7} />
                <div className="text-xs uppercase tracking-[0.18em] text-ink/45 font-semibold">{c.label}</div>
                <div className="text-sm font-semibold text-ink mt-2 leading-relaxed break-words">{c.value}</div>
              </a>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 mt-16 items-start">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-ink/8 shadow-lift h-[26rem]" data-testid="contact-map">
              <iframe
                title="Invesmo — Camberwell VIC 3124"
                src="https://maps.google.com/maps?q=Camberwell%20VIC%203124%20Australia&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white border border-ink/8 p-8 sm:p-10">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <CheckCircle2 size={44} className="text-brand mx-auto mb-6" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl font-bold text-ink">Message received.</h3>
                  <p className="text-sm text-ink/55 mt-3 max-w-xs mx-auto leading-relaxed">
                    Thanks {form.name.split(" ")[0]} — Anu will be in touch within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <h3 className="font-display text-2xl font-bold text-ink mb-8">Send an enquiry</h3>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} />
                    <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
                  </div>
                  <input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={`${inputCls} mt-5`} />
                  <textarea data-testid="contact-message-input" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" className={`${inputCls} mt-5 resize-none`} />
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={!valid || sending}
                    className="mt-7 w-full rounded-full bg-obsidian text-gold px-8 py-4 text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-gold hover:text-obsidian hover:shadow-gold-glow disabled:opacity-40 disabled:pointer-events-none inline-flex items-center justify-center gap-3"
                  >
                    {sending && <Loader2 size={16} className="animate-spin" />}
                    Send message
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
